import { p as pipe } from '../ReadonlyNonEmptyArray-0e481d0b.js';
import { r as replace, t as toUpperCase } from '../string-40bcd6bf.js';
import { merge, cloneDeep } from 'lodash';
import { Node_Type, TextStyle_TextAlignment, LATEST_VERSION, Link_Target, Decoration_Type } from 'ricos-schema';
import { v4 } from 'uuid';
import { fromEntries } from '../utils.js';
import { generateId } from './generateRandomId.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const createNode = (type, {
  nodes,
  data,
  style,
  id
}) => __spreadProps(__spreadValues({
  type,
  id: id != null ? id : generateId(),
  nodes
}, dataByNodeType(type, data)), {
  style
});
const dataByNodeType = (type, data) => ({
  [Node_Type.CODE_BLOCK]: { codeBlockData: data },
  [Node_Type.DIVIDER]: { dividerData: data },
  [Node_Type.HEADING]: { headingData: data },
  [Node_Type.FILE]: { fileData: data },
  [Node_Type.GALLERY]: { galleryData: data },
  [Node_Type.GIF]: { gifData: data },
  [Node_Type.HTML]: { htmlData: data },
  [Node_Type.IMAGE]: { imageData: data },
  [Node_Type.BUTTON]: { buttonData: data },
  [Node_Type.MAP]: { mapData: data },
  [Node_Type.PARAGRAPH]: { paragraphData: data },
  [Node_Type.POLL]: { pollData: data },
  [Node_Type.TEXT]: { textData: data },
  [Node_Type.VIDEO]: { videoData: data },
  [Node_Type.AUDIO]: { audioData: data },
  [Node_Type.EMBED]: { embedData: data },
  [Node_Type.APP_EMBED]: { appEmbedData: data },
  [Node_Type.LINK_PREVIEW]: { linkPreviewData: data },
  [Node_Type.TABLE]: { tableData: data },
  [Node_Type.TABLE_CELL]: { tableCellData: data },
  [Node_Type.COLLAPSIBLE_LIST]: { collapsibleListData: data },
  [Node_Type.BULLETED_LIST]: {
    bulletedListData: data
  },
  [Node_Type.ORDERED_LIST]: {
    orderedListData: data
  }
})[type];
const createParagraphNode = (nodes = [], data, style) => createNode(Node_Type.PARAGRAPH, {
  nodes,
  data: __spreadValues({
    textStyle: { textAlignment: TextStyle_TextAlignment.AUTO }
  }, data),
  style
});
const createTextNode = (text, decorations = []) => createNode(Node_Type.TEXT, { nodes: [], id: "", data: { text, decorations } });
const createHeadingNode = (nodes = [], data) => createNode(Node_Type.HEADING, {
  nodes,
  data: __spreadValues({
    textStyle: { textAlignment: TextStyle_TextAlignment.AUTO }
  }, data)
});
const createDecoration = (type, data = {}) => __spreadValues({ type }, data);
const initializeMetadata = ({
  version,
  id = v4(),
  createdTimestamp,
  updatedTimestamp
} = {}) => ({
  version: version || LATEST_VERSION,
  createdTimestamp: createdTimestamp || /* @__PURE__ */ new Date(),
  updatedTimestamp: updatedTimestamp || /* @__PURE__ */ new Date(),
  id
});
const reduceDecorations = (decorations) => {
  const reducedDecorationsMap = decorations.reduce(
    (decorationMap, _a) => {
      var _b = _a, { type } = _b, data = __objRest(_b, ["type"]);
      const currentDecoration = decorationMap[type] || { type };
      const nextDecoration = __spreadValues({ type }, data);
      return __spreadProps(__spreadValues({}, decorationMap), {
        [type]: merge(currentDecoration, nextDecoration)
      });
    },
    {}
  );
  const reducedDecorations = Object.values(reducedDecorationsMap);
  return reducedDecorations;
};
const toLinkTarget = (target = "BLANK") => {
  switch (pipe(target, toUpperCase, replace("_", ""))) {
    case "SELF": {
      return Link_Target.SELF;
    }
    case "BLANK": {
      return Link_Target.BLANK;
    }
    case "PARENT": {
      return Link_Target.PARENT;
    }
    case "TOP": {
      return Link_Target.TOP;
    }
    default: {
      return Link_Target.BLANK;
    }
  }
};
const createLink = ({
  url,
  rel,
  target,
  anchor
}) => {
  const relValues = (typeof rel === "string" ? rel : "").split(" ").filter((key) => ["nofollow", "sponsored", "ugc", "noreferrer"].includes(key)).map((key) => [key, true]) || [];
  return {
    anchor,
    url,
    rel: relValues.length > 0 ? fromEntries(relValues) : void 0,
    target: toLinkTarget(typeof target === "string" ? target : void 0)
  };
};
const parseLink = ({
  url,
  rel,
  target,
  anchor,
  customData
}) => ({
  anchor,
  url,
  rel: rel && Object.entries(rel).flatMap(([key, value]) => value ? key : []).join(" "),
  target: target && "_" + target.toLowerCase(),
  customData
});
const createLinkDecoration = (data) => createDecoration(Decoration_Type.LINK, { linkData: { link: createLink(data) } });
const last = (arr) => arr.length > 0 ? arr[arr.length - 1] : null;
const partitionBy = (isSeparator, isPartition, Separator, Partition, addToPartition) => (nodes) => nodes.reduce((partitions, node) => {
  if (isSeparator(node)) {
    partitions.push(Separator(node));
  } else {
    let lastPartition = last(partitions);
    if (!lastPartition || !isPartition(lastPartition)) {
      const partition = Partition(node);
      partitions.push(partition);
      lastPartition = last(partitions);
    }
    addToPartition(lastPartition, node);
  }
  return partitions;
}, []);
const getRangeStyle = (range) => {
  try {
    const style = JSON.parse(range.style);
    return { styleKey: Object.keys(style)[0], styleValue: Object.values(style)[0] };
  } catch (e) {
    return { styleKey: range.style, styleValue: range.style };
  }
};
const removeOverlappingRangesWithSameStyle = (ranges) => {
  const isOverlapping = (a, b) => a.styleKey === b.styleKey && a.styleValue === b.styleValue && a.offset <= b.offset + b.length && a.offset + a.length >= b.offset;
  return ranges.map((range) => __spreadValues(__spreadValues({}, range), getRangeStyle(range))).reduce((acc, range) => {
    const filteredRanges = acc.filter((r) => {
      return !(range.styleKey === r.styleKey && r.offset >= range.offset && r.offset + r.length <= range.offset + range.length);
    });
    return mergeRangeWithList(filteredRanges, range, isOverlapping);
  }, []).reduce((acc, range) => {
    return adjustAndAddRange(acc, range);
  }, []).map((_a) => {
    var _b = _a, range = __objRest(_b, ["styleKey", "styleValue"]);
    return range;
  });
};
const adjustAndAddRange = (existingRanges, newRange) => {
  const isOverlapping = (a, b) => (a.styleKey === b.styleKey || `NOT_${a.styleKey}` === b.styleKey || a.styleKey === `NOT_${b.styleKey}`) && a.offset <= b.offset + b.length && a.offset + a.length >= b.offset;
  const adjust = (existing, newR) => {
    if (isOverlapping(existing, newR)) {
      if (existing.offset === newR.offset) {
        return __spreadProps(__spreadValues({}, existing), {
          offset: newR.offset + newR.length,
          length: existing.length - newR.length
        });
      } else if (existing.offset > newR.offset) {
        const overlapLength = newR.offset + newR.length - existing.offset;
        return __spreadProps(__spreadValues({}, existing), {
          offset: existing.offset + overlapLength,
          length: existing.length - overlapLength
        });
      } else {
        return __spreadProps(__spreadValues({}, existing), { length: newR.offset - existing.offset });
      }
    }
    return existing;
  };
  const adjustedRanges = existingRanges.map((range) => adjust(range, newRange)).filter((range) => range.length > 0);
  adjustedRanges.push(newRange);
  return adjustedRanges;
};
const mergeOverlappingEntityRanges = (entityRanges) => {
  const isOverlapping = (a, b) => a.key === b.key && a.offset <= b.offset + b.length && a.offset + a.length >= b.offset;
  return entityRanges.reduce(
    (acc, range) => mergeRangeWithList(acc, range, isOverlapping),
    []
  );
};
const mergeRangeWithList = (ranges, newRange, isOverlapping) => {
  const mergeRanges = (a, b) => __spreadProps(__spreadValues({}, a), {
    offset: Math.min(a.offset, b.offset),
    length: Math.max(a.offset + a.length, b.offset + b.length) - Math.min(a.offset, b.offset)
  });
  const result = ranges.reduce(
    (acc, currentRange) => {
      if (isOverlapping(acc.currentMerging, currentRange)) {
        acc.currentMerging = mergeRanges(acc.currentMerging, currentRange);
        return acc;
      }
      acc.ranges.push(currentRange);
      return acc;
    },
    { ranges: [], currentMerging: newRange }
  );
  result.ranges.push(result.currentMerging);
  return result.ranges;
};
const generateRangeMap = (ranges) => {
  const rangeMap = {};
  ranges.forEach((_a) => {
    var _b = _a, { offset, length } = _b, props = __objRest(_b, ["offset", "length"]);
    rangeMap[offset] = [...rangeMap[offset] || [], __spreadProps(__spreadValues({ action: "start" }, props), { length })];
    rangeMap[offset + length] = [
      ...rangeMap[offset + length] || [],
      __spreadProps(__spreadValues({ action: "end" }, props), { length })
    ];
  });
  const normalizedRangeMap = normalizeRangeMap(rangeMap);
  if (!normalizedRangeMap[0]) {
    normalizedRangeMap[0] = [];
  }
  return normalizedRangeMap;
};
const getRangeEntityId = (entry) => {
  if ("key" in entry) {
    return entry.key;
  }
  return entry.style;
};
const isEntryOverlapping = (current, next) => {
  return getRangeEntityId(current) === getRangeEntityId(next) && current.action === "end" && next.action === "start" && // Ranges of length 1 are not overlapping
  current.length !== 1 && next.length !== 1;
};
const getStyleKeyVariation = (entry) => {
  if ("key" in entry) {
    return entry.key;
  }
  try {
    const style = JSON.parse(entry.style);
    return Object.keys(style)[0];
  } catch (e) {
    return entry.style;
  }
};
const getStyleKeyValue = (entry) => {
  if ("key" in entry) {
    return entry.key;
  }
  return entry.style;
};
const mergeAdjacentRanges = (rangeMap) => {
  const newRangeMap = cloneDeep(rangeMap);
  Object.keys(newRangeMap).forEach((key) => {
    const intKey = parseInt(key);
    if (!newRangeMap[intKey + 1]) {
      return;
    }
    const hasMultipleSameStyleEntries = newRangeMap[intKey].some((current) => {
      return newRangeMap[intKey].some(
        (next) => current === next ? false : getStyleKeyVariation(current) === getStyleKeyVariation(next)
      );
    });
    if (hasMultipleSameStyleEntries) {
      return;
    }
    const currentRangeBeforeUpdate = newRangeMap[intKey];
    newRangeMap[intKey] = newRangeMap[intKey].filter((current) => {
      return newRangeMap[intKey + 1].every((next) => !isEntryOverlapping(current, next));
    });
    newRangeMap[intKey + 1] = newRangeMap[intKey + 1].filter((next) => {
      return currentRangeBeforeUpdate.every((current) => !isEntryOverlapping(current, next));
    });
  });
  return Object.entries(newRangeMap).reduce((acc, [key, value]) => {
    return __spreadProps(__spreadValues({}, acc), {
      [key]: value.map((v) => __spreadProps(__spreadValues({}, v), { length: void 0 }))
    });
  }, {});
};
const moveEndOfRanges = (rangeMap) => {
  const newRangeMap = cloneDeep(rangeMap);
  Object.keys(newRangeMap).reverse().forEach((key, index, allKeys) => {
    for (const current of newRangeMap[key]) {
      if (current.action !== "end") {
        continue;
      }
      const currentStyleKey = getStyleKeyVariation(current);
      let isMovable = false;
      let hasValidStart = false;
      const sameStyleStartInSameKey = newRangeMap[key].find((entry) => {
        return getStyleKeyValue(entry) === getStyleKeyValue(current) && entry.action === "start";
      });
      if (sameStyleStartInSameKey) {
        newRangeMap[key] = newRangeMap[key].filter(
          (entry) => ![current, sameStyleStartInSameKey].includes(entry)
        );
        continue;
      }
      for (let i = index + 1; i < allKeys.length; i++) {
        const prevKey = allKeys[i];
        hasValidStart = newRangeMap[prevKey].some((prev) => {
          return prev.action === "start" && getRangeEntityId(current) === getRangeEntityId(prev);
        });
        if (hasValidStart) {
          break;
        }
        isMovable = newRangeMap[prevKey].some((prev) => {
          const prevStyleKey = getStyleKeyVariation(prev);
          return prev.action === "start" && currentStyleKey === prevStyleKey && getRangeEntityId(current) !== getRangeEntityId(prev);
        });
        if (isMovable) {
          newRangeMap[prevKey].push(current);
          break;
        }
      }
      if (hasValidStart) {
        continue;
      }
      if (isMovable) {
        newRangeMap[key] = newRangeMap[key].filter((entry) => entry !== current);
      }
    }
  });
  return newRangeMap;
};
const normalizeRangeMap = (rangeMap) => {
  const newRangeMap = moveEndOfRanges(mergeAdjacentRanges(rangeMap));
  Object.keys(newRangeMap).forEach((key) => {
    var _a;
    if (((_a = newRangeMap[key]) == null ? void 0 : _a.length) === 0) {
      delete newRangeMap[key];
    }
  });
  return newRangeMap;
};
const getNodeFullText = (node) => {
  var _a, _b;
  return ((_a = node.textData) == null ? void 0 : _a.text) ? node.textData.text : ((_b = node.nodes) == null ? void 0 : _b.length) ? node.nodes.map(getNodeFullText).join("") : "";
};

export { createDecoration, createHeadingNode, createLink, createLinkDecoration, createNode, createParagraphNode, createTextNode, dataByNodeType, generateRangeMap, getNodeFullText, initializeMetadata, last, mergeOverlappingEntityRanges, normalizeRangeMap, parseLink, partitionBy, reduceDecorations, removeOverlappingRangesWithSameStyle, toLinkTarget };
//# sourceMappingURL=nodeUtils.js.map
